import { useEffect } from 'react';
import { hasValue } from '@famly/stat_ts-utils_has-value';
import { usePlatform } from '@famly/stat_ts-utils_platform-context';
/**
 * Handle clicks outside of your component! Use this to close popups, modals, etc
 *
 * @deprecated Use the `ClickAwayListener` component from
 * `@famly/mf_util_click-away-listener` instead. It wraps the element you care
 * about directly, so there is no ref to wire up. See that package's README for
 * a migration guide.
 *
 * Note that the `ignoreSelectors` would find any element on the screen that
 * matches any of the selectors. With `newSelectorBehavior` true only elements
 * on the path from the clicked element up to the dom root are tested for the
 * selectors.
 *
 * @example
 * ```
 * const Foo = props => {
 *
 *   const ref = React.useRef(undefined);
 *
 *   useOnClickOutside(ref, props.bar, ['.ignore-this-modal', '#ignoreThisButton']);
 *
 *   return <MyComponent {...} ref={ref}/>
 * }
 * ```
 */
export const useOnClickOutside = (node, callback, ignoreSelectors, capture, event, newSelectorBehavior) => {
    const { isTouch } = usePlatform();
    const eventName = getEventNameOrDefault(event, isTouch);
    useEffect(() => {
        const listener = event => {
            /**
             * This implementation uses event delegation on the document to catch any and
             * all events. These events carry a "target" node, which can be used
             * to determine if the click event was targeted at a node
             * inside or outside of the attached element.
             *
             * Checking for value of event.target and node.current is just a fallback
             * that shouldn't really happen. The real logic is in the `contains` call.
             */
            if (!hasValue(event.target) || !hasValue(node.current)) {
                return;
            }
            if (node.current.contains(event.target)) {
                return;
            }
            /**
             * If "ignore selectors" are passed to it, we have to traverse upwards in the DOM tree, from
             * the event target to see if any parent element matches one of the selectors.
             */
            if (hasValue(ignoreSelectors) &&
                event.target instanceof Element &&
                ignoreSelectors.some(selector => newSelectorBehavior
                    ? isElementContainedInSelector(event.target, selector)
                    : isElementContainedFromSelector(event.target, selector))) {
                return;
            }
            callback(event);
        };
        document.addEventListener(eventName, listener, capture);
        return function () {
            document.removeEventListener(eventName, listener, capture);
        };
    }, [node, callback, ignoreSelectors, newSelectorBehavior, eventName, capture]);
};
// Use traversal function to check if the selector matches an element inside `element` or inside all ancestors to `element`.
const isElementContainedFromSelector = (element, selector) => {
    return hasValue(traverseElementUpwards(element, element => element.querySelector(selector)));
};
// Use traversal function to check if the selector matches `element` or an ancestor of `element`.
const isElementContainedInSelector = (element, selector) => {
    return hasValue(traverseElementUpwards(element, element => (element.matches(selector) ? true : undefined)));
};
// Traverse upwards a DOM tree starting with any Element.
// Return if `cb` returns a value or if no parents exist.
export const traverseElementUpwards = (element, cb) => {
    const returnValue = cb(element);
    if (hasValue(returnValue)) {
        return returnValue;
    }
    const parentElement = element.parentElement;
    if (hasValue(parentElement)) {
        return traverseElementUpwards(parentElement, cb);
    }
    return null;
};
/**
 * `touchstart` and `mouseup` are the default events used in this hook. These events were chosen as we wanted
 * a hook that behaved similar to `react-onclickoutside`.
 *
 * However, it is possible to choose other events if so desired.
 */
const getEventNameOrDefault = (eventName, isTouch) => {
    if (hasValue(eventName)) {
        return eventName;
    }
    if (isTouch) {
        return 'touchstart';
    }
    return 'mouseup';
};
